import React from 'react';
import { Button, Input } from 'antd';
import {
	LockOutlined,
	HomeOutlined,
	MailOutlined,
	CopyOutlined,
	CheckOutlined,
	SendOutlined,
	InfoCircleOutlined,
	ReloadOutlined,
	LinkOutlined,
	ClockCircleOutlined,
	ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PremiumErrorSurface } from './PremiumErrorSurface';
import { useAccessRequestFlow, JUSTIFICATION_MIN, JUSTIFICATION_MAX } from './useAccessRequestFlow';
import { useDenialBranding } from './useDenialBranding';
import { useI18n } from '../contexts/LocalizationContext';

const { TextArea } = Input;

/**
 * Context captured from the PDP evaluation response for a denied route.
 * Routers extract this from `evaluation.context` and the `X-Request-ID` header.
 */
export interface DenialContext {
	decisionId?: string;
	correlationId?: string;
	reasonUser?: string;
	evaluatedAt?: string;
}

interface AccessDeniedErrorProps {
	pluginId: string;
	pluginName?: string;
	reason?: 'pdp' | 'not_found' | 'disabled';
	requestableRoles?: string[];
	deniedPath?: string;
	denialContext?: DenialContext;
	hasAccessCatalog?: boolean;
	/**
	 * Admin-authored reason the app is disabled (e.g. the manifest `migration_note`
	 * from the incumbent quarantine flow). When provided, it is shown on the
	 * disabled surface instead of leaving the user with only the generic notice.
	 */
	disabledReason?: string;
}

function generateClientReferenceId(): string {
	const random = Math.random().toString(36).slice(2, 8).toUpperCase();
	return `LOCAL-${Date.now().toString(36).toUpperCase()}-${random}`;
}

export const AccessDeniedError: React.FC<AccessDeniedErrorProps> = ({
	pluginId,
	pluginName,
	reason = 'pdp',
	requestableRoles = [],
	deniedPath,
	denialContext,
	hasAccessCatalog = true,
	disabledReason,
}) => {
	const navigate = useNavigate();
	const { t } = useI18n();
	const displayName = pluginName || pluginId.replace(/-/g, ' ');
	const [copied, setCopied] = React.useState(false);

	const trimmedDisabledReason = React.useMemo(() => {
		const value = (disabledReason || '').trim();
		return value.length > 0 ? value : null;
	}, [disabledReason]);

	const referenceId = React.useMemo(() => {
		return denialContext?.decisionId
			|| denialContext?.correlationId
			|| generateClientReferenceId();
	}, [denialContext]);

	const isRealReferenceId = !!(denialContext?.decisionId || denialContext?.correlationId);

	const safeRoles = React.useMemo(
		() => Array.from(new Set((requestableRoles || []).filter(Boolean))),
		[requestableRoles],
	);

	const showRoleHints = React.useMemo(() => {
		const envValue = String((import.meta as any).env?.VITE_FEATURE_SHOW_REQUESTABLE_ROLES || '').toLowerCase();
		const globalValue = (window as any).__EXPERIENCE_FEATURES__?.showRequestableRoles;
		const localValue = localStorage.getItem('experience.features.showRequestableRoles');
		const envOverride = envValue === 'true' ? true : envValue === 'false' ? false : null;
		const globalOverride = typeof globalValue === 'boolean' ? globalValue : null;
		const localOverride =
			localValue === 'true' ? true :
			localValue === 'false' ? false :
			null;
		return localOverride ?? globalOverride ?? envOverride ?? true;
	}, []);

	const branding = useDenialBranding();
	const hasSupportContact = !!(branding.supportEmail || branding.supportUrl);

	const requestFlow = useAccessRequestFlow({
		pluginId,
		pluginName: displayName,
		requestableRoles: safeRoles,
		correlationId: referenceId,
		deniedPath,
	});

	const canRequestAccess = reason === 'pdp' && safeRoles.length > 0;

	const openMyRequests = React.useCallback(() => {
		navigate('/access-catalog/my-requests');
	}, [navigate]);

	const openAppsLibrary = React.useCallback(() => {
		navigate('/applications');
	}, [navigate]);

	const retryCurrentLocation = React.useCallback(() => {
		window.location.reload();
	}, []);

	const openSupportContact = React.useCallback(() => {
		if (branding.supportUrl) {
			window.open(branding.supportUrl, '_blank', 'noopener');
			return;
		}
		if (branding.supportEmail) {
			const subject = reason === 'disabled'
				? `Request to Enable App: ${displayName}`
				: `Access Denied: ${displayName}`;
			const body = [
				'Hello,',
				'',
				`I received an access denial while trying to open ${displayName}.`,
				'',
				`Decision Reference: ${referenceId}`,
				deniedPath ? `Path: ${deniedPath}` : '',
				denialContext?.reasonUser ? `Reason: ${denialContext.reasonUser}` : '',
				'',
				'Could you help me resolve this?',
				'',
				'Thank you!',
			].filter(Boolean).join('\n');
			window.location.href = `mailto:${branding.supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
		}
	}, [branding, displayName, reason, referenceId, deniedPath, denialContext]);

	const copyReferenceId = React.useCallback(async () => {
		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(referenceId);
				setCopied(true);
				window.setTimeout(() => setCopied(false), 1800);
				return;
			}
		} catch {
			// Intentionally fall through to prompt fallback.
		}
		window.prompt('Copy reference ID:', referenceId);
	}, [referenceId]);

	const getContent = () => {
		switch (reason) {
			case 'pdp':
				return {
					icon: <LockOutlined />,
					title: 'Access Denied',
					tone: 'warning' as const,
					message: denialContext?.reasonUser ? (
						<>
							<p>You don't have permission to access <strong>{displayName}</strong>.</p>
							<p className="premium-error__reason">{denialContext.reasonUser}</p>
						</>
					) : (
						<p>You don't have permission to access <strong>{displayName}</strong>. You can request access below or contact support if you believe this is an error.</p>
					),
				};
			case 'disabled':
				return {
					icon: <LockOutlined />,
					title: t('shell.error.pluginDisabledTitle', { pluginName: displayName }),
					tone: 'error' as const,
					message: (
						<>
							<p>{t('shell.error.pluginDisabledDescription')}</p>
							{trimmedDisabledReason && (
								<p className="premium-error__reason">{trimmedDisabledReason}</p>
							)}
							<p>{t('shell.error.pluginDisabledContactHint')}</p>
						</>
					),
				};
			case 'not_found':
			default:
				return {
					icon: <LockOutlined />,
					title: 'App Not Available',
					tone: 'neutral' as const,
					message: (
						<p><strong>{displayName}</strong> is not installed in this environment. Check the Apps Library for available applications.</p>
					),
				};
		}
	};

	const content = getContent();
	const charsNeeded = Math.max(0, JUSTIFICATION_MIN - requestFlow.justification.trim().length);
	const errorTestId = reason === 'disabled'
		? 'shell-error-plugin-disabled'
		: reason === 'not_found'
			? 'shell-error-plugin-not-found'
			: 'shell-error-access-denied';

	return (
		<PremiumErrorSurface
			icon={content.icon}
			title={content.title}
			description={content.message}
			tone={content.tone}
			testId={errorTestId}
		>
			{/* ── Primary action: Request Access (only when real roles exist) ── */}
			{canRequestAccess && requestFlow.state !== 'submitted' && (
				<section className="premium-error__section premium-error__request-form premium-error__fade-in">
					<h2 className="premium-error__section-title">
						<SendOutlined className="premium-error__section-icon" />
						Request Access
					</h2>

					{showRoleHints && safeRoles.length > 0 && (
						<div className="premium-error__badge-list">
							{safeRoles.map(role => (
								<span key={role} className="premium-error__badge">{role}</span>
							))}
						</div>
					)}

					{requestFlow.preview && (
						<div className="premium-error__preview">
							<ClockCircleOutlined className="premium-error__section-icon" />
							Approval: {requestFlow.preview.approval_chain?.[0]?.approver_name || 'Manager review'}
							{requestFlow.preview.estimated_total_hours != null && (
								<span> &middot; ~{requestFlow.preview.estimated_total_hours}h</span>
							)}
							{(requestFlow.preview.similar_requests ?? 0) > 0 && (
								<span> &middot; {requestFlow.preview.similar_requests} similar requests recently</span>
							)}
						</div>
					)}

					<TextArea
						value={requestFlow.justification}
						onChange={e => requestFlow.setJustification(e.target.value)}
						placeholder="Why do you need access? (e.g., I need to review Q3 audit reports)"
						autoSize={{ minRows: 2, maxRows: 4 }}
						maxLength={JUSTIFICATION_MAX}
						disabled={requestFlow.state === 'submitting'}
						className="premium-error__justification"
					/>
					<div className="premium-error__char-count">
						{charsNeeded > 0
							? `${charsNeeded} more character${charsNeeded === 1 ? '' : 's'} needed`
							: `${requestFlow.justification.trim().length} / ${JUSTIFICATION_MAX}`}
					</div>

					{requestFlow.error && (
						<div className="premium-error__inline-error" role="alert">
							{requestFlow.error}
						</div>
					)}

					<Button
						type="primary"
						icon={<SendOutlined />}
						onClick={requestFlow.submit}
						disabled={!requestFlow.canSubmit}
						loading={requestFlow.state === 'submitting'}
						block
						size="large"
					>
						{requestFlow.state === 'submitting' ? 'Submitting...' : 'Submit Access Request'}
					</Button>
				</section>
			)}

			{/* Post-submission confirmation */}
			{requestFlow.state === 'submitted' && requestFlow.result && (
				<section className="premium-error__section premium-error__status--success premium-error__fade-in" role="status" aria-live="polite">
					<div className="premium-error__success-icon" aria-hidden="true">
						<CheckOutlined />
					</div>
					<h2 className="premium-error__section-title premium-error__text-center">
						Request Submitted
					</h2>
					<p className="premium-error__text-center premium-error__text-muted">
						Your request is being processed. You&rsquo;ll be notified when it&rsquo;s approved.
					</p>
					<dl className="premium-error__dl premium-error__dl--compact">
						<dt>Request ID</dt>
						<dd>{requestFlow.result.request_id}</dd>
						{requestFlow.result.status && (
							<>
								<dt>Status</dt>
								<dd>{requestFlow.result.status}</dd>
							</>
						)}
						{requestFlow.result.approval_chain?.[0] && (
							<>
								<dt>Next Approver</dt>
								<dd>{requestFlow.result.approval_chain[0].approver_name}</dd>
							</>
						)}
						{requestFlow.result.estimated_completion && (
							<>
								<dt>Est. Completion</dt>
								<dd>{requestFlow.result.estimated_completion}</dd>
							</>
						)}
					</dl>
					{hasAccessCatalog && (
						<Button
							icon={<LinkOutlined />}
							onClick={openMyRequests}
							block
							className="premium-error__mt-12"
						>
							Track This Request
						</Button>
					)}
				</section>
			)}

			{/* ── Secondary actions: clear visual hierarchy ── */}
			<div className="premium-error__actions">
				{/* Primary: Retry is most common intent after denial */}
				<Button size="large" icon={<ReloadOutlined />} onClick={retryCurrentLocation}>
					Retry
				</Button>

				{reason === 'pdp' && hasAccessCatalog && requestFlow.state !== 'submitted' && (
					<Button size="large" onClick={openMyRequests}>
						View My Requests
					</Button>
				)}

				{reason !== 'pdp' && (
					<Button size="large" onClick={openAppsLibrary}>
						Open Applications
					</Button>
				)}

				{reason === 'disabled' && hasSupportContact && (
					<Button type="primary" size="large" icon={<MailOutlined />} onClick={openSupportContact}>
						Contact Administrator
					</Button>
				)}
			</div>

			{/* ── Tertiary links: low-priority navigation ── */}
			<div className="premium-error__links">
				{hasSupportContact && reason !== 'disabled' && (
					<button type="button" className="premium-error__link" onClick={openSupportContact}>
						<MailOutlined className="premium-error__link-icon" />
						{branding.supportLabel}
					</button>
				)}
				<button type="button" className="premium-error__link" onClick={() => navigate('/')}>
					<ArrowLeftOutlined className="premium-error__link-icon" />
					Back to Dashboard
				</button>
			</div>

			{/* ── Technical Details: collapsed by default, for support/debugging ── */}
			<details className="premium-error__details">
				<summary className="premium-error__details-summary">
					<InfoCircleOutlined className="premium-error__section-icon" />
					Technical Details
				</summary>
				<div className="premium-error__details-body">
					<dl className="premium-error__dl">
						{denialContext?.decisionId && (
							<>
								<dt>Decision ID</dt>
								<dd>
									{denialContext.decisionId}
									<button
										type="button"
										className="premium-error__copy-inline"
										onClick={copyReferenceId}
										aria-label="Copy decision ID"
									>
										{copied ? <CheckOutlined /> : <CopyOutlined />}
									</button>
								</dd>
							</>
						)}
						{denialContext?.correlationId && (
							<>
								<dt>Correlation ID</dt>
								<dd>{denialContext.correlationId}</dd>
							</>
						)}
						{denialContext?.evaluatedAt && (
							<>
								<dt>Evaluated At</dt>
								<dd>{denialContext.evaluatedAt}</dd>
							</>
						)}
						{denialContext?.reasonUser && (
							<>
								<dt>Policy Reason</dt>
								<dd>{denialContext.reasonUser}</dd>
							</>
						)}
						<dt>Plugin</dt>
						<dd>{pluginId}</dd>
						{reason === 'disabled' && trimmedDisabledReason && (
							<>
								<dt>Disable Reason</dt>
								<dd>{trimmedDisabledReason}</dd>
							</>
						)}
						{deniedPath && (
							<>
								<dt>Denied Path</dt>
								<dd>{deniedPath}</dd>
							</>
						)}
						{safeRoles.length > 0 && (
							<>
								<dt>Required Roles</dt>
								<dd>{safeRoles.join(', ')}</dd>
							</>
						)}
						{!isRealReferenceId && (
							<>
								<dt>Reference</dt>
								<dd>{referenceId} (client-generated &mdash; PDP context was not captured)</dd>
							</>
						)}
					</dl>
				</div>
			</details>
		</PremiumErrorSurface>
	);
};
